import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
  EntityAdapter,
  EntityState,
  Slice,
} from '@reduxjs/toolkit'
import api from '@services/api.service'

export interface EntityStateWithLoadingAndError<T> extends EntityState<T> {
  loading: boolean
  inProgress: string[]
  error: string | null
  requestId: string | null // Used only in job reducer to get results for latest filter request
}

export interface ModelWithId {
  id?: string
}

// Generic API service functions
const createGenericThunks = <T>(entityName: string, endpoint: string) => {
  return {
    fetchAll: createAsyncThunk(`${entityName}/fetchAll`, async () => {
      const response = await api.get(`${endpoint}/`)
      return response.data
    }),
    fetchById: createAsyncThunk(
      `${entityName}/fetchById`,
      async (id: string) => {
        const response = await api.get(`${endpoint}/${id}/`)
        return response.data
      }
    ),
    create: createAsyncThunk(`${entityName}/`, async (data: T) => {
      const response = await api.post(`${endpoint}/`, data)
      return response.data
    }),
    update: createAsyncThunk(
      `${entityName}/update`,
      async ({ id, data }: { id: string; data: T }) => {
        const response = await api.put(`${endpoint}/${id}/`, data)
        return response.data
      }
    ),
    delete: createAsyncThunk(`${entityName}/delete`, async (id: string) => {
      await api.delete(`${endpoint}/${id}/`)
      return id // Returning the ID of the deleted entity
    }),
  }
}

// Create a generic entity adapter and export it
const createGenericAdapter = <T>() => createEntityAdapter<T>()

// Generic Slice Factory
export const createGenericSlice = <T extends ModelWithId>(
  entityName: string,
  entityEndpoint: string,
  initialState: Partial<EntityStateWithLoadingAndError<T>>,
  extraReducers?: (builder: any) => void
) => {
  const adapter: EntityAdapter<T> = createGenericAdapter<T>()
  const thunks = createGenericThunks<T>(entityName, entityEndpoint)

  const slice: Slice<EntityStateWithLoadingAndError<T>> = createSlice({
    name: entityName,
    initialState: adapter.getInitialState({
      loading: false,
      inProgress: [],
      error: null,
      requestId: null,
      ...initialState,
    }),
    reducers: {
      // @ts-ignore
      addOne: adapter.addOne, // @ts-ignore
      addMany: adapter.addMany, // @ts-ignore
      removeAll: adapter.removeAll, // @ts-ignore
      removeOne: adapter.removeOne, // @ts-ignore
      removeMany: adapter.removeMany, // @ts-ignore
      updateOne: adapter.updateOne, // @ts-ignore
      updateMany: adapter.updateMany, // @ts-ignore
      upsertOne: adapter.upsertOne, // @ts-ignore
      upsertMany: adapter.upsertMany, // @ts-ignore
      setAll: adapter.setAll, // @ts-ignore
      setOne: adapter.setOne, // @ts-ignore
      setMany: adapter.setMany,
    },
    extraReducers: (builder) => {
      // Fetch all entities
      builder
        .addCase(thunks.fetchAll.pending, (state) => {
          state.loading = true
          state.error = null
        })
        .addCase(thunks.fetchAll.fulfilled, (state, action) => {
          // @ts-ignore
          adapter.setAll(state, action.payload)
          state.loading = false
        })
        .addCase(thunks.fetchAll.rejected, (state, action) => {
          state.loading = false
          state.error = action.error.message || 'Failed to fetch data'
        })

      // Fetch single entity by ID
      builder
        .addCase(thunks.fetchById.pending, (state, action) => {
          // @ts-ignore
          state.inProgress = [action.meta.arg, ...state.inProgress]
          state.error = null
        })
        .addCase(thunks.fetchById.fulfilled, (state, action) => {
          // @ts-ignore
          adapter.upsertOne(state, action.payload)
          state.inProgress = state.inProgress.filter(
            (id) => id !== action.meta.arg
          )
        })
        .addCase(thunks.fetchById.rejected, (state, action) => {
          state.inProgress = state.inProgress.filter(
            (id) => id !== action.meta.arg
          )
          state.error = action.error.message || 'Failed to fetch data'
        })

      // Create new entity
      builder
        .addCase(thunks.create.pending, (state) => {
          state.loading = true
          state.error = null
        })
        .addCase(thunks.create.fulfilled, (state, action) => {
          // @ts-ignore
          adapter.addOne(state, action.payload)
          state.loading = false
        })
        .addCase(thunks.create.rejected, (state, action) => {
          state.loading = false
          state.error = action.error.message || 'Failed to create data'
        })

      // Update existing entity
      builder
        .addCase(thunks.update.pending, (state, action) => {
          state.inProgress = [action.meta.arg.id, ...state.inProgress]
          state.error = null
        })
        .addCase(thunks.update.fulfilled, (state, action) => {
          // @ts-ignore
          adapter.updateOne(state, {
            id: action.meta.arg.id,
            changes: action.payload,
          })
          state.inProgress = state.inProgress.filter(
            (id) => id !== action.meta.arg.id
          )
        })
        .addCase(thunks.update.rejected, (state, action) => {
          state.inProgress = state.inProgress.filter(
            (id) => id !== action.meta.arg.id
          )
          state.error = action.error.message || 'Failed to update data'
        })

      // Delete entity
      builder
        .addCase(thunks.delete.pending, (state, action) => {
          state.inProgress = [action.meta.arg, ...state.inProgress]
          state.error = null
        })
        .addCase(thunks.delete.fulfilled, (state, action) => {
          // @ts-ignore
          adapter.removeOne(state, action.meta.arg)
          state.inProgress = state.inProgress.filter(
            (id) => id !== action.meta.arg
          )
        })
        .addCase(thunks.delete.rejected, (state, action) => {
          state.inProgress = state.inProgress.filter(
            (id) => id !== action.meta.arg
          )
          state.error = action.error.message || 'Failed to delete data'
        })

      if (extraReducers) extraReducers(builder)
    },
  })

  const { actions } = slice

  const { setDetailId } = actions

  return { slice, thunks, adapter, setDetailId }
}
